import type { TAuthPresetSpec, TSignatureParse } from '../../entities/auth-preset';

export type TSignedShape = 'body' | 'time-dot-body' | 'method-path-body';

export const SHAPE_TEMPLATE: Record<TSignedShape, string> = {
  body: '{rawBody}',
  'time-dot-body': '{timestamp}.{rawBody}',
  'method-path-body': '{method}{path}{rawBody}',
};

export const shapeOfTemplate = (template: string): TSignedShape => {
  const found = (Object.keys(SHAPE_TEMPLATE) as TSignedShape[]).find(
    (shape) => SHAPE_TEMPLATE[shape] === template,
  );
  return found ?? 'body';
};

export type TTimePlace = 'same_header' | 'header';

export const KV_TIME_KEY = 't';
export const KV_SIGNATURE_KEY = 'v1';

export const DEFAULT_SKEW_SEC = 300;
const MAX_SKEW_SEC = 86_400;

/** Text, not a number: a field being emptied to retype it is not a zero. */
export const skewSeconds = (text: string): number | null => {
  const trimmed = text.trim();
  if (!/^\d{1,5}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= MAX_SKEW_SEC ? value : null;
};

export interface IAnswers {
  name: string;
  algorithm: TAuthPresetSpec['signature']['algorithm'];
  encoding: TAuthPresetSpec['signature']['encoding'];
  header: string;
  shape: TSignedShape;
  prefix: string;
  timePlace: TTimePlace;
  timeHeader: string;
  skewSec: string;
}

export const signsTime = (answers: IAnswers): boolean => answers.shape === 'time-dot-body';

export const answersToSpec = (answers: IAnswers, base: TAuthPresetSpec): TAuthPresetSpec => {
  const timed = signsTime(answers);
  const sameHeader = timed && answers.timePlace === 'same_header';

  // The time placement also decides the parse kind: a header read as one value has no fields to hold a time.
  const parse: TSignatureParse = sameHeader
    ? {
        kind: 'kv',
        pairSeparator: ',',
        kvSeparator: '=',
        signatureKey: KV_SIGNATURE_KEY,
        acceptAny: true,
      }
    : answers.prefix
      ? { kind: 'prefixed', prefix: answers.prefix }
      : { kind: 'raw' };

  const skew = skewSeconds(answers.skewSec);

  return {
    ...base,
    name: answers.name,
    key: base.key,
    signature: {
      ...base.signature,
      algorithm: answers.algorithm,
      encoding: answers.encoding,
      template: SHAPE_TEMPLATE[answers.shape],
      location: { header: answers.header, parse },
    },
    timestamp: timed
      ? {
          location: sameHeader
            ? { kind: 'same_header', key: KV_TIME_KEY }
            : { kind: 'header', header: answers.timeHeader },
          format: 'unix_seconds',
          ...(skew === null ? {} : { defaultToleranceSec: skew }),
        }
      : { location: { kind: 'none' } },
  };
};

export const answersFromSpec = (spec: TAuthPresetSpec): IAnswers => ({
  name: spec.name,
  algorithm: spec.signature.algorithm,
  encoding: spec.signature.encoding,
  header: spec.signature.location.header,
  shape: shapeOfTemplate(spec.signature.template),
  prefix:
    spec.signature.location.parse.kind === 'prefixed' ? spec.signature.location.parse.prefix : '',
  timePlace: spec.timestamp.location.kind === 'header' ? 'header' : 'same_header',
  timeHeader: spec.timestamp.location.kind === 'header' ? spec.timestamp.location.header : '',
  skewSec: String(spec.timestamp.defaultToleranceSec ?? DEFAULT_SKEW_SEC),
});
